/**
 * Global in-flight request limiter for API calls.
 *
 * Why this exists:
 * - API host (fb.dataklout.com) speaks HTTP/1.1 only (no HTTP/2).
 * - Browsers allow ~6 parallel connections per host on HTTP/1.1.
 * - Homepage / app often fires 12–20 APIs at once → extras sit in Network "Pending"
 *   (Queueing), and the server can get slower under concurrent load than a single
 *   Postman request.
 *
 * We cap concurrency ourselves so:
 * 1. Critical calls aren't stuck behind a wall of non-critical ones.
 * 2. Backend sees fewer simultaneous hits → each response closer to Postman times.
 * 3. Network tab shows fewer long-Pending requests.
 */

const DEFAULT_MAX_CONCURRENT = 4;

let maxConcurrent = DEFAULT_MAX_CONCURRENT;
let active = 0;
/** @type {{ task: () => Promise<any>, resolve: Function, reject: Function, priority: number }[]} */
const queue = [];

function sortQueue() {
  // Higher priority first; stable enough for our use (small queue).
  queue.sort((a, b) => b.priority - a.priority);
}

function pump() {
  while (active < maxConcurrent && queue.length > 0) {
    sortQueue();
    const item = queue.shift();
    if (!item) break;
    active += 1;
    Promise.resolve()
      .then(item.task)
      .then(item.resolve, item.reject)
      .finally(() => {
        active -= 1;
        pump();
      });
  }
}

/**
 * Run an async task when a concurrency slot is free.
 * @param {() => Promise<any>} task
 * @param {{ priority?: number }} [options] priority: higher runs first (default 0)
 */
export function enqueueRequest(task, options = {}) {
  const priority = options.priority ?? 0;
  return new Promise((resolve, reject) => {
    queue.push({ task, resolve, reject, priority });
    pump();
  });
}

export function setMaxConcurrentRequests(n) {
  maxConcurrent = Math.max(1, Math.min(6, Number(n) || DEFAULT_MAX_CONCURRENT));
  pump();
}

export function getRequestQueueStats() {
  return { active, queued: queue.length, maxConcurrent };
}
